import { useState, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import { useAuth } from "@/hooks/useAuth";
import { resizeImage } from "@/lib/imageResize";
import { uploadFile } from "@/lib/directUpload";
import { LocationAutocomplete } from "@/components/ui/LocationAutocomplete";
import type { User } from "@/types";

const UPDATE_PROFILE_MUTATION = gql`
  mutation UpdateProfile(
    $displayName: String
    $bio: String
    $avatarSignedId: String
    $latitude: Float
    $longitude: Float
    $city: String
    $address: String
    $defaultDelivery: Boolean
    $defaultPickup: Boolean
    $defaultPickupRadius: Int
  ) {
    updateProfile(
      displayName: $displayName
      bio: $bio
      avatarSignedId: $avatarSignedId
      latitude: $latitude
      longitude: $longitude
      city: $city
      address: $address
      defaultDelivery: $defaultDelivery
      defaultPickup: $defaultPickup
      defaultPickupRadius: $defaultPickupRadius
    ) {
      user {
        id
        email
        displayName
        bio
        avatarUrl
        rating
        ratingsCount
        createdAt
        admin
        activeListingsCount
        latitude
        longitude
        city
        address
        defaultDelivery
        defaultPickup
        defaultPickupRadius
      }
      errors
    }
  }
`;

export function EditProfilePage() {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();

  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarSignedId, setAvatarSignedId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Location & delivery defaults
  const [locationCity, setLocationCity] = useState("");
  const [locationAddress, setLocationAddress] = useState("");
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [defaultDelivery, setDefaultDelivery] = useState(true);
  const [defaultPickup, setDefaultPickup] = useState(true);
  const [defaultPickupRadius, setDefaultPickupRadius] = useState("0");

  useEffect(() => {
    if (user && !initialized) {
      setDisplayName(user.displayName);
      setBio(user.bio || "");
      setLocationCity(user.city || "");
      setLocationAddress(user.address || "");
      setLatitude(user.latitude);
      setLongitude(user.longitude);
      setDefaultDelivery(user.defaultDelivery ?? true);
      setDefaultPickup(user.defaultPickup ?? true);
      setDefaultPickupRadius(String(user.defaultPickupRadius ?? 0));
      setInitialized(true);
    }
  }, [user, initialized]);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show local preview immediately
    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);

    try {
      setUploading(true);
      const resized = await resizeImage(file);
      const signedId = await uploadFile(resized);
      setAvatarSignedId(signedId);
    } catch (err) {
      console.error("Avatar upload failed:", err);
      setAvatarPreview(null);
      setAvatarSignedId(null);
    } finally {
      setUploading(false);
    }
  };

  const updateMutation = useMutation({
    mutationFn: (vars: {
      displayName?: string;
      bio?: string;
      avatarSignedId?: string;
      latitude?: number | null;
      longitude?: number | null;
      city?: string;
      address?: string;
      defaultDelivery?: boolean;
      defaultPickup?: boolean;
      defaultPickupRadius?: number;
    }) =>
      graphqlClient.request<{
        updateProfile: { user: User | null; errors: string[] };
      }>(UPDATE_PROFILE_MUTATION, vars),
    onSuccess: (data) => {
      if (data.updateProfile.user) {
        updateUser(data.updateProfile.user);
        setErrors([]);
        setSuccess(true);
        setAvatarSignedId(null);
        setAvatarPreview(null);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setErrors(data.updateProfile.errors);
        setSuccess(false);
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    setSuccess(false);

    updateMutation.mutate({
      displayName: displayName.trim() || undefined,
      bio: bio.trim() || undefined,
      avatarSignedId: avatarSignedId || undefined,
      latitude,
      longitude,
      city: locationCity || undefined,
      address: locationAddress || undefined,
      defaultDelivery,
      defaultPickup,
      defaultPickupRadius: defaultPickupRadius ? parseInt(defaultPickupRadius, 10) : 0,
    });
  };

  const avatarSrc = avatarPreview || user?.avatarUrl;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <h1 className="mb-8 text-2xl font-bold text-gray-900">
        {t("settings.title")}
      </h1>

      {errors.length > 0 && (
        <div className="mb-6 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {errors.map((err, i) => (
            <p key={i}>{err}</p>
          ))}
        </div>
      )}

      {success && (
        <div className="mb-6 rounded-lg bg-green-50 p-3 text-sm text-green-700">
          {t("settings.profile.update_success")}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="rounded-lg border border-gray-200 bg-white">
          {/* Avatar row */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5">
            <span className="text-sm font-semibold text-gray-900">
              {t("settings.profile.photo")}
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleAvatarClick}
                className="group relative cursor-pointer"
                disabled={uploading}
              >
                {avatarSrc ? (
                  <img
                    src={avatarSrc}
                    alt={user?.displayName || ""}
                    className="h-12 w-12 rounded-full object-cover transition-opacity group-hover:opacity-75"
                  />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-800 text-lg font-bold text-white transition-opacity group-hover:opacity-75">
                    {user?.displayName?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
                {uploading && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/30">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  </div>
                )}
              </button>
              <button
                type="button"
                onClick={handleAvatarClick}
                disabled={uploading}
                className="text-sm font-medium text-primary-800 hover:text-primary-900"
              >
                {t("settings.profile.change_photo")}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </div>

          {/* Display name row */}
          <div className="flex flex-col gap-2 border-b border-gray-200 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <label
              htmlFor="displayName"
              className="text-sm font-semibold text-gray-900"
            >
              {t("settings.profile.display_name")}
            </label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="input sm:w-72"
              required
              minLength={2}
            />
          </div>

          {/* Bio row */}
          <div className="flex flex-col gap-2 border-b border-gray-200 px-6 py-5">
            <label
              htmlFor="bio"
              className="text-sm font-semibold text-gray-900"
            >
              {t("settings.profile.about")}
            </label>
            <textarea
              id="bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={3}
              placeholder={t("settings.profile.about_placeholder")}
              className="input"
            />
          </div>

          {/* Location row */}
          <div className="flex flex-col gap-2 border-b border-gray-200 px-6 py-5">
            <LocationAutocomplete
              label={t("settings.profile.location", "Vietovė")}
              value={locationCity}
              latitude={latitude}
              longitude={longitude}
              radius={Number(defaultPickupRadius)}
              onChange={(data) => {
                setLocationCity(data.city || data.address);
                setLocationAddress(data.address);
                setLatitude(data.latitude);
                setLongitude(data.longitude);
              }}
              placeholder="pvz. Vilnius"
            />
          </div>

          {/* Delivery defaults */}
          <div className="flex flex-col gap-4 border-b border-gray-200 px-6 py-5">
            <span className="text-sm font-semibold text-gray-900">
              {t("settings.profile.delivery_defaults", "Pristatymo numatytieji")}
            </span>
            <p className="text-xs text-gray-500">
              {t("settings.profile.delivery_defaults_hint", "Šie nustatymai bus automatiškai pritaikyti kuriant naujus skelbimus.")}
            </p>

            {/* Delivery toggle */}
            <div className="flex items-center gap-3">
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={defaultDelivery}
                  onChange={(e) => setDefaultDelivery(e.target.checked)}
                  className="peer sr-only"
                />
                <div className="h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-800 peer-checked:after:translate-x-full peer-checked:after:border-white" />
              </label>
              <span className="text-sm text-gray-700">
                {t("settings.profile.default_delivery", "Galimas pristatymas")}
              </span>
            </div>

            {/* Pickup toggle */}
            <div className="flex items-center gap-3">
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={defaultPickup}
                  onChange={(e) => setDefaultPickup(e.target.checked)}
                  className="peer sr-only"
                />
                <div className="h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-800 peer-checked:after:translate-x-full peer-checked:after:border-white" />
              </label>
              <span className="text-sm text-gray-700">
                {t("settings.profile.default_pickup", "Galimas asmeninis atsiėmimas")}
              </span>
            </div>

            {/* Pickup radius (only when pickup is enabled) */}
            {defaultPickup && (
              <div className="ml-14">
                <label className="mb-1 block text-sm text-gray-600">
                  {t("settings.profile.pickup_radius", "Atsiėmimo spindulys (km)")}
                </label>
                <select
                  value={defaultPickupRadius}
                  onChange={(e) => setDefaultPickupRadius(e.target.value)}
                  className="input w-40"
                >
                  <option value="0">Tik mieste</option>
                  <option value="1">1 km</option>
                  <option value="2">2 km</option>
                  <option value="3">3 km</option>
                  <option value="5">5 km</option>
                  <option value="10">10 km</option>
                  <option value="15">15 km</option>
                  <option value="-1">Visoje Lietuvoje</option>
                </select>
              </div>
            )}
          </div>

          {/* Email row */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5">
            <span className="text-sm font-semibold text-gray-900">
              {t("settings.profile.email")}
            </span>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">{user?.email}</span>
              <Link
                to="/profile/change-email"
                className="text-sm font-medium text-primary-800 hover:text-primary-900"
              >
                {t("settings.profile.change_email")}
              </Link>
            </div>
          </div>

          {/* Password row */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5">
            <span className="text-sm font-semibold text-gray-900">
              {t("settings.profile.password")}
            </span>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">••••••••</span>
              <Link
                to="/profile/change-password"
                className="text-sm font-medium text-primary-800 hover:text-primary-900"
              >
                {t("settings.profile.change_password")}
              </Link>
            </div>
          </div>

          {/* Notification settings row */}
          <div className="flex items-center justify-between px-6 py-5">
            <span className="text-sm font-semibold text-gray-900">
              {t("settings.notifications.title")}
            </span>
            <Link
              to="/profile/notifications"
              className="text-sm font-medium text-primary-800 hover:text-primary-900"
            >
              {t("settings.notifications.link")}
            </Link>
          </div>
        </div>

        {/* Save button */}
        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={updateMutation.isPending || uploading}
            className="btn-primary"
          >
            {updateMutation.isPending
              ? t("settings.profile.updating")
              : t("settings.profile.update_button")}
          </button>
        </div>
      </form>
    </div>
  );
}
